/**
 * Insights Lab — the analyst's workshop, at /insights-lab (took the address
 * from the management page 2026-09-07; /analytics-collaboration redirects):
 *   1. Analytics Collaboration  (NL query canvas + project workspace)
 *   2. Build a dashboard        (compose widgets on one synced playhead)
 *   3. Segment Workbench        (5-minute windows, scoped stats)
 *   4. Insights Playground      (ad-hoc SQL + visuals)
 *   5. Pitch Playground         (vertical pitch layers)
 *   6. Visual Builder           (analysis authoring over the semantic catalog)
 *
 * The active project's org and game pre-fill the playground tabs so the
 * person lands on the game they chose. Projects are per-person, server-side,
 * and one can be marked as default so the page opens pre-loaded.
 *
 * Alongside the tabs, the header offers a Match viewer for the project's
 * game: the bare match viewer (?view=video, MatchVideoOnly — the video and its
 * playback controls: play, skip, speed, scrubber with KO/HT/FT; Ben,
 * 2026-10-14) hosted in a docked frame, or opened as a floating window.
 *
 * Staff-only. The server enforces this on every API call the embedded pages
 * make; the page shows a quiet notice for anyone who reaches it by URL.
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { trackEvent } from '../lib/analytics'
import { type Session } from '../lib/api'
import { getClubGames, getClubs, type CachedClub, type CachedGame } from '../lib/clubsCache'
import { openViewerWindow } from '../packs/videoHandoff'
import {
  listProjects, createProject, updateProject, deleteProject, duplicateProject,
  type AnalyticsProject, type CanvasDraft,
} from '../lib/projects'
import CanvasWorkspace from './CanvasWorkspace'
// Playground tabs removed from UI (2026-09-08) — code retained, imports
// commented to avoid unused-import errors. See docs/sdlc-follow-ups.md.
// import InsightsPlayground from './InsightsPlayground'
// import InsightsPitchPlayground from './InsightsPitchPlayground'
import VisualBuilder from './VisualBuilder'
import SegmentWorkbench from './segment/SegmentWorkbench'
import ChatPanel from '../playground/ChatPanel'
import StackDataNotice from '../playground/StackDataNotice'
import './insights-playground.css'  // for pg-chat-* styles
import { pathSeg } from '../lib/urls'
import { useViewerSeek } from './match/viewerBridge'

// Lazy like GameMatchSummary in App.tsx, and for the same reason: the
// dashboard carries react-grid-layout and the match-page panels, which a
// staff-only tab must not add to every customer's main bundle.
const LabDashboard = lazy(() => import('./dashboard/LabDashboard'))
// Lazy for the same bundle reason: charts.tsx and the section cards are
// staff-only weight.
const AttackingStructureLab = lazy(() => import('./AttackingStructureLab'))
// Its thin defending-side sibling — same lazy reasoning.
const TeamDefensiveControlLab = lazy(() => import('./TeamDefensiveControlLab'))
const ProvokePressLab = lazy(() => import('./ProvokePressLab'))
// v2 is a deliberate copy of the page above — the surface the hover,
// clip-panel and definition-sandbox work lands on, so v1 stays as a
// coach reads it. Lazy for the same bundle reason as its siblings.
const ProvokePressLabV2 = lazy(() => import('./ProvokePressLabV2'))
const FormationDynamicsLab = lazy(() => import('./FormationDynamicsLab'))
const PossessionFlowLab = lazy(() => import('./PossessionFlowLab'))
const DataDictionary = lazy(() => import('./DataDictionary'))

import './admin.css'
import './insights-lab.css'

export type CollabTab = 'collaborate' | 'dashboard' | 'playground' | 'pitch' | 'builder' | 'segment' | 'attstructure' | 'defcontrol' | 'provoke' | 'provoke2' | 'formations' | 'flow' | 'dictionary'

// `hidden` keeps a section routable without giving it a tab in the bar.
// The entry must stay in TABS either way: activeTab below resolves the
// route's tab against this list and falls back to TABS[0], so deleting an
// entry does not remove the page — it silently serves Collaborate instead.
const TABS: { tab: CollabTab; path: string; label: string; sub: string; hidden?: boolean }[] = [
  {
    tab: 'collaborate',
    path: '/insights-lab',
    label: 'Analytics Collaboration',
    sub: 'Ask questions in natural language. Results appear as cards you can rearrange and visualize.',
  },
  {
    tab: 'dashboard',
    path: '/insights-lab/dashboard',
    label: 'Build a dashboard',
    sub: 'Compose your own board from the widget palette — video, spatial '
      + 'control, heat, passing networks, timelines and the opportunity '
      + 'feed — all following one playhead. Layout is saved per game.',
  },
  {
    tab: 'segment',
    path: '/insights-lab/segment',
    label: 'Segment Workbench',
    sub: 'Analyze a game in 5-minute windows with scoped moments, pass networks, and tactical stats.',
  },
  // Insights Playground and Pitch Playground tabs removed from the UI
  // (2026-09-08). Code is retained — see docs/sdlc-follow-ups.md.
  {
    tab: 'attstructure',
    path: '/insights-lab/attacking-structure',
    label: 'Attacking Structure',
    sub: 'The attacking_structure pack report as live queries: every section '
      + 'shows its SQL, runs on demand, and compares against the report\u2019s '
      + 'promoted baseline for the bound game.',
  },
  {
    tab: 'defcontrol',
    path: '/insights-lab/defensive-control',
    label: 'Defensive Control',
    sub: 'The def-SOP report as live queries: the out-of-possession '
      + 'universe, block shape by zone, the ball against our own block, '
      + 'press bands and peaks, and the regain ladder (upper bounds).',
  },
  {
    tab: 'provoke',
    path: '/insights-lab/provoke-press',
    label: 'Provoke',
    sub: 'The provoke-then-exploit principle: detected provocation '
      + 'attempts classified five ways, by zone, opponent shape and '
      + 'pressing features, with every attempt’s full chain.',
  },
  {
    tab: 'provoke2',
    path: '/insights-lab/provoke-press-v2',
    label: 'Provoke v2',
    sub: 'The same report as above, as the surface for what is being '
      + 'tried on top of it: hover detail on every data point, the clip '
      + 'behind an attempt, and an editable class definition.',
  },
  {
    tab: 'formations',
    path: '/insights-lab/formation-dynamics',
    label: 'Formation Dynamics',
    sub: 'How each team\u2019s shape moves through a match: the shape at any '
      + 'second, the formation sequence in and out of possession, and a '
      + 'summary. Layer-stack games only.',
  },
  {
    tab: 'flow',
    path: '/insights-lab/possession-flow',
    label: 'Possession Flow',
    sub: 'A wind map of how each team moves the ball: its habitual routes '
      + 'as currents, by half, 15-minute window or score state, with where '
      + 'the ball went by channel and third. Layer-stack games only.',
  },
  {
    tab: 'dictionary',
    path: '/insights-lab/data-dictionary',
    label: 'Data Dictionary',
    sub: 'What every table in the Lab holds, in plain English, and what each '
      + 'of its columns means — the names your queries use.',
    // Reached from the 'Data dictionary' link beside the Tables heading in
    // the canvas table browser, which is where you are when the question
    // ("what is in this table?") actually comes up — not from the tab bar.
    hidden: true,
  },
  {
    tab: 'builder',
    path: '/insights-lab/builder',
    label: 'Visual Builder',
    sub: 'Build an analysis by choosing. The question you are asking is written '
      + 'below it, in the form the engine actually runs.',
  },
]

export default function AnalyticsCollaboration({ session, tab }: { session: Session; tab: CollabTab }) {
  const isStaff = session.user.is_staff || session.user.is_platform_admin

  useEffect(() => {
    // The Insights Lab was never instrumented on the SPA (Mixpanel
    // audit, 2026-10-20). One event at the shell covers every tab —
    // playground, pitch, builder, segment, dashboard, collaborate —
    // so a tab added later cannot be forgotten.
    trackEvent('Insights Lab Opened', { tab })
  }, [tab])

  const [projects, setProjects] = useState<AnalyticsProject[]>([])
  const [sharedProjects, setSharedProjects] = useState<AnalyticsProject[]>([])
  const [active, setActive] = useState<AnalyticsProject | null>(null)
  const [loading, setLoading] = useState(true)
  const [_error, setError] = useState<string | null>(null)

  // Club list for the create/edit form
  const [clubs, setClubs] = useState<CachedClub[]>([])


  // The Match viewer pane: the bare ?view=video viewer for the
  // active project's game, hosted in a docked same-origin frame — just the
  // video and its playback controls, as in the Insights Workshop.
  const [matchPane, setMatchPane] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const playgroundSqlRef = useRef<((sql: string) => void) | null>(null)
  /** Ref for adding a card from the AI chat panel onto the canvas. */
  const addCardRef = useRef<((question: string, sql: string, visual?: string, explanation?: string) => void) | null>(null)
  /** Ref for toggling the CanvasWorkspace table browser sidebar. */
  const toggleTablesRef = useRef<(() => void) | null>(null)
  /** Ref to the match viewer iframe so Clip Finder can seek the video. */
  const matchIframeRef = useRef<HTMLIFrameElement>(null)


  /** Seek the match viewer pane (Clip Finder), opening it when closed.
   *  useViewerSeek holds the seek until the pane says it is ready — a
   *  fixed delay lost it on a slow load. */
  /** The floated window, while it is open: seeks go there, not to a
   *  pane re-docked beside it. */
  const matchWindow = useRef<Window | null>(null)
  const matchTarget = useCallback((): Window | null => {
    const w = matchWindow.current
    if (w && !w.closed) return w
    return matchPane ? matchIframeRef.current?.contentWindow ?? null : null
  }, [matchPane])
  const openMatchPane = useCallback(() => setMatchPane(true), [])
  const { seek: seekMatchViewer, reset: resetMatchViewer, notice: matchNotice } =
    useViewerSeek(matchTarget, openMatchPane)
  // Another game reloads the pane: not ready until the new page says so.
  // Only a switch counts — the first game arriving must not clear a seek
  // already made for it.
  const paneGame = useRef<string | null>(null)
  useEffect(() => {
    const code = active?.game_code ?? null
    if (paneGame.current !== null && code !== paneGame.current) {
      matchWindow.current?.close()
      matchWindow.current = null
      resetMatchViewer()
    }
    paneGame.current = code
  }, [active?.game_code, resetMatchViewer])

  const refresh = useCallback(() => {
    setLoading(true)
    listProjects()
      .then(({ projects: ps, shared_projects: sps }) => {
        setProjects(ps)
        setSharedProjects(sps ?? [])
        // Auto-select the default, or keep the current selection alive —
        // in either list (a shared project can be the active one).
        const def = ps.find((p) => p.is_default)
        setActive((prev) => {
          if (prev) {
            const still = ps.find((p) => p.id === prev.id)
              ?? (sps ?? []).find((p) => p.id === prev.id)
            if (still) return still
          }
          return def ?? null
        })
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load projects'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (isStaff) {
      refresh()
      getClubs().then(setClubs).catch(() => setClubs([]))
    }
  }, [isStaff, refresh])

  if (!isStaff) {
    return (
      <>
        <h1 className="page-title">Insights Lab</h1>
        <p className="page-sub">Priori staff only.</p>
      </>
    )
  }

  const activeTab = TABS.find((t) => t.tab === tab) ?? TABS[0]!

  const clubLabel = (() => {
    if (!active?.org_id) return null
    const c = clubs.find((x) => x.id === active.org_id)
    return c ? c.code : `Org ${active.org_id}`
  })()

  /** CanvasWorkspace owns the table browser and is the only thing that ever
   *  sets toggleTablesRef, so the Tables button is live only where the canvas
   *  is mounted. Mirrors the render condition below exactly — a ref cannot be
   *  read for this, since mutating .current never re-renders the bar. */
  const canvasMounted = activeTab.tab === 'collaborate'
    && !!active?.org_id && !!active?.game_code

  /** The match page's viewer mode for the active project's game. */
  const matchViewerUrl = active?.game_code
    ? `/match-room/${pathSeg(active.game_code)}?view=video`
    : null

  /** Float the viewer: a real browser window on the same URL the docked
   *  frame shows. Named per game so a re-click focuses the open window
   *  instead of stacking copies. */
  const floatMatchViewer = () => {
    if (!matchViewerUrl || !active?.game_code) return
    resetMatchViewer()
    // through the one opener: grant in the address, window per view-as
    matchWindow.current = openViewerWindow(matchViewerUrl, active.game_code)
    setMatchPane(false)
  }

  return (
    <div className={`insights-lab${chatOpen ? ' pg-chat-open' : ''}`}>
      <div className="lab-eyebrow">Insights Lab &middot; Priori staff only</div>
      <div className="cv-page-head">
        <h1 className="page-title">Insights Lab</h1>
        <ProjectDropdown
          projects={projects}
          sharedProjects={sharedProjects}
          active={active}
          clubs={clubs}
          loading={loading}
          onSelect={setActive}
          onRefresh={refresh}
          clubLabel={clubLabel}
        />
        {active && active.can_edit === false && (
          <span className="ac-badge" title="A shared project you don't own — you can look, not save">
            read-only{active.owner_name ? ` \u00b7 ${active.owner_name}` : ''}
          </span>
        )}
        <nav className="lab-tabs lab-tabs--inline" aria-label="Insights Lab sections">
          {TABS.filter((t) => !t.hidden).map((t) => (
            <Link key={t.tab} to={t.path}
                  className={t.tab === activeTab.tab ? 'is-on' : ''}
                  aria-current={t.tab === activeTab.tab ? 'page' : undefined}>
              {t.label}
            </Link>
          ))}
        </nav>
        <span className="cv-plan-buttons">
          {active?.org_id && active?.game_code && !matchPane && (
            <button className="ac-btn-sm" onClick={() => {
              // docking takes over from any floated window
              matchWindow.current = null
              resetMatchViewer()
              setMatchPane(true)
            }}
                    title="The game's video and pitch with full match controls">
              Match viewer</button>
          )}
          {canvasMounted && (
            <button className="ac-btn-sm"
                    title="Show or hide the table browser"
                    onClick={() => toggleTablesRef.current?.()}>
              Tables
            </button>
          )}
          <button className="pg-chat-toggle"
                  onClick={() => setChatOpen((v) => !v)}>
            {chatOpen ? 'Close AI' : 'Ask AI'}
          </button>
        </span>
      </div>

      {activeTab.tab === 'collaborate' && (
        <StackDataNotice gameCode={active?.game_code ?? null}
                         orgId={active?.org_id ?? null}
                         what="The canvas, table list and assistant" />
      )}

      <div className="cv-main-area">
        <div className="cv-main-content">
          {activeTab.tab === 'collaborate' && (
            <>
              {active && active.org_id && active.game_code && (
                <CanvasWorkspace
                  key={active.id}
                  project={active}
                  orgId={active.org_id}
                  gameCode={active.game_code}
                  readOnly={active.can_edit === false}
                  addCardRef={addCardRef}
                  toggleTablesRef={toggleTablesRef}
                  onSeekVideo={seekMatchViewer}
                  onDraftChange={(draft: CanvasDraft) => {
                    setActive((prev) => prev ? { ...prev, draft } : prev)
                  }}
                />
              )}
              {(!active || !active.org_id || !active.game_code) && (
                <div className="cv-empty">
                  <p>{!active
                    ? 'Select or create a project from the dropdown above.'
                    : 'This project needs a club and game. Click the dropdown to edit it.'}</p>
                </div>
              )}
            </>
          )}
          {activeTab.tab === 'dashboard' && (
            <Suspense fallback={<p className="lab-sub">Loading the dashboard&hellip;</p>}>
              <LabDashboard session={session}
                initialOrg={active?.org_id ?? undefined}
                initialGame={active?.game_code ?? undefined} />
            </Suspense>
          )}
          {/* Playground and Pitch tabs removed from UI — code retained */}
          {activeTab.tab === 'attstructure' && (
            <Suspense fallback={<p className="lab-sub">Loading the report&hellip;</p>}>
              <AttackingStructureLab session={session} />
            </Suspense>
          )}
          {activeTab.tab === 'defcontrol' && (
            <Suspense fallback={<p className="lab-sub">Loading the report&hellip;</p>}>
              <TeamDefensiveControlLab session={session} initialOrg={active?.org_id ?? undefined}
                initialGame={active?.game_code ?? undefined} />
            </Suspense>
          )}
          {activeTab.tab === 'provoke' && (
            <Suspense fallback={<p className="lab-sub">Loading the report&hellip;</p>}>
              <ProvokePressLab session={session} />
            </Suspense>
          )}
          {activeTab.tab === 'provoke2' && (
            <Suspense fallback={<p className="lab-sub">Loading the report&hellip;</p>}>
              <ProvokePressLabV2 session={session} initialOrg={active?.org_id ?? undefined}
                initialGame={active?.game_code ?? undefined} />
            </Suspense>
          )}
          {activeTab.tab === 'formations' && (
            <Suspense fallback={<p className="lab-sub">Loading formation dynamics&hellip;</p>}>
              <FormationDynamicsLab session={session} initialOrg={active?.org_id ?? undefined}
                initialGame={active?.game_code ?? undefined} />
            </Suspense>
          )}
          {activeTab.tab === 'flow' && (
            <Suspense fallback={<p className="lab-sub">Loading possession flow&hellip;</p>}>
              <PossessionFlowLab session={session} initialOrg={active?.org_id ?? undefined}
                initialGame={active?.game_code ?? undefined} />
            </Suspense>
          )}
          {activeTab.tab === 'dictionary' && (
            <Suspense fallback={<p className="lab-sub">Loading the data dictionary&hellip;</p>}>
              <DataDictionary session={session} />
            </Suspense>
          )}
          {activeTab.tab === 'builder' && (
            <VisualBuilder session={session} embedded />
          )}
          {activeTab.tab === 'segment' && (
            <SegmentWorkbench session={session}
              gameCode={active?.game_code ?? null}
              orgId={active?.org_id ?? null} />
          )}
        </div>

        {matchPane && matchViewerUrl && (
          <aside className="cv-side-panes">
            <div className="cv-pane cv-pane--match">
              <div className="cv-pane__head">
                <strong>Match viewer</strong>
                <span className="cv-plan-dock__actions">
                  <button className="ac-btn-sm" onClick={floatMatchViewer}
                          title="Open in a floating window">&#x2197;</button>
                  <button className="ac-btn-sm"
                          onClick={() => { resetMatchViewer(); setMatchPane(false) }}>&times;</button>
                </span>
              </div>
              {/* The match page's own viewer mode, same-origin. Everything
                  in it — sync, tick marks, layers, the per-pane PiP
                  pop-outs — is the real page's machinery, not a copy. */}
              {matchNotice && <p className="pg-note" role="status">{matchNotice}</p>}
              <iframe className="cv-pane__frame" src={matchViewerUrl}
                      ref={matchIframeRef}
                      title={`Match viewer — ${active?.game_code ?? ''}`} />
            </div>
          </aside>
        )}


      </div>
      {/* Docked chat panel — renders alongside the content */}
      {chatOpen && (
        <div className="pg-chat-drawer">
          <ChatPanel
            gameCode={active?.game_code ?? null}
            orgId={active?.org_id ?? null}
            projectId={active?.id ?? null}
            onUseQuery={(sql) => {
              playgroundSqlRef.current?.(sql)
            }}
            onAddCard={activeTab.tab === 'collaborate'
              ? (question, sql, visual, explanation) => { addCardRef.current?.(question, sql, visual, explanation) }
              : undefined}
          />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Project dropdown — compact selector in the header
// ---------------------------------------------------------------------------

function ProjectDropdown({ projects, sharedProjects, active, clubs, loading, onSelect, onRefresh, clubLabel }: {
  projects: AnalyticsProject[]
  sharedProjects: AnalyticsProject[]
  active: AnalyticsProject | null
  clubs: CachedClub[]
  loading: boolean
  onSelect: (p: AnalyticsProject | null) => void
  onRefresh: () => void
  clubLabel: string | null
}) {
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<AnalyticsProject | null>(null)
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false); setCreating(false); setEditing(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleCreate = async (title: string, orgId: number | null, gameCode: string | null) => {
    setBusy(true); setFormError(null)
    try {
      const { project } = await createProject({
        title, org_id: orgId, game_code: gameCode || null,
        is_default: projects.length === 0,
      })
      onSelect(project)
      onRefresh()
      setCreating(false)
      setOpen(false)
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Could not create')
    } finally { setBusy(false) }
  }

  const handleUpdate = async (id: number, title: string, orgId: number | null, gameCode: string | null) => {
    setBusy(true); setFormError(null)
    try {
      const { project } = await updateProject(id, {
        title, org_id: orgId, game_code: gameCode || null,
      })
      onSelect(project)
      onRefresh()
      setEditing(null)
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Could not save')
    } finally { setBusy(false) }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this project?')) return
    setBusy(true)
    try {
      await deleteProject(id)
      if (active?.id === id) onSelect(null)
      onRefresh()
    } catch { /* ignore */ }
    finally { setBusy(false) }
  }

  /** Mark a project as the default (auto-opens on Insights Lab load). */
  const handleSetDefault = async (p: AnalyticsProject) => {
    if (p.is_default) return  // already default
    setBusy(true)
    try {
      const { project } = await updateProject(p.id, { is_default: true })
      if (active?.id === p.id) onSelect(project)
      onRefresh()
    } catch { /* ignore */ }
    finally { setBusy(false) }
  }

  /** Flip a project between private and shared (owner-only server-side). */
  const handleShareToggle = async (p: AnalyticsProject) => {
    setBusy(true)
    try {
      const { project } = await updateProject(p.id, { shared: !p.shared })
      if (active?.id === p.id) onSelect(project)
      onRefresh()
    } catch { /* ignore */ }
    finally { setBusy(false) }
  }

  /** Copy a project (own or shared) into a new one the caller owns; the
   *  copy opens immediately as the active project. */
  const handleDuplicate = async (p: AnalyticsProject) => {
    const title = prompt('Name for the copy:', `${p.title} (copy)`)
    if (title === null) return // canceled
    setBusy(true)
    try {
      const { project } = await duplicateProject(p.id, title.trim() || undefined)
      onSelect(project)
      setOpen(false)
      onRefresh()
    } catch { /* ignore */ }
    finally { setBusy(false) }
  }

  const clubName = (orgId: number | null) => {
    if (!orgId) return null
    const c = clubs.find((x) => x.id === orgId)
    return c ? c.code : `Org ${orgId}`
  }

  return (
    <div className="cv-project-dropdown" ref={ref}>
      <button className="cv-project-dropdown__trigger" onClick={() => setOpen(!open)}>
        {loading ? 'Loading\u2026' : active ? (
          <>
            <strong>{active.title}</strong>
            {active.game_code && (
              <span className="muted">{clubLabel} &mdash; {active.game_code}</span>
            )}
          </>
        ) : (
          <span className="muted">No project selected</span>
        )}
        <span className="cv-project-dropdown__chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>

      {open && (
        <div className="cv-project-dropdown__menu">
          <button className="cv-dd-item cv-dd-item--new"
                  onClick={() => { setCreating(true); setEditing(null) }}>
            + New project
          </button>

          {creating && (
            <div className="cv-dd-form">
              <ProjectForm clubs={clubs} busy={busy} error={formError}
                onSave={handleCreate}
                onCancel={() => setCreating(false)} />
            </div>
          )}

          {projects.length === 0 && !creating && (
            <p className="cv-dd-empty muted">No projects yet.</p>
          )}

          {projects.map((p) => (
            <div key={p.id} className={`cv-dd-item${active?.id === p.id ? ' is-active' : ''}`}>
              <button className="cv-dd-item__select" onClick={() => {
                onSelect(p); setOpen(false); setEditing(null); setCreating(false)
              }}>
                <span className="cv-dd-item__title">{p.title}</span>
                {p.is_default && <span className="ac-badge">default</span>}
                {p.shared && <span className="ac-badge">shared</span>}
                {p.game_code && (
                  <span className="muted">{clubName(p.org_id)} &mdash; {p.game_code}</span>
                )}
              </button>
              <span className="cv-dd-item__actions">
                {!p.is_default && (
                  <button className="ac-btn-sm"
                          title="Set as default — auto-opens when you open Insights Lab"
                          onClick={(e) => { e.stopPropagation(); void handleSetDefault(p) }}>
                    Set default
                  </button>
                )}
                <button className="ac-btn-sm"
                        title={p.shared
                          ? 'Shared: every Lab user can open it read-only. Click to make it private.'
                          : 'Private: only you can see it. Click to share it with Lab users.'}
                        onClick={(e) => { e.stopPropagation(); handleShareToggle(p) }}>
                  {p.shared ? 'Make private' : 'Share'}
                </button>
                <button className="ac-btn-sm" title="Duplicate under a new name"
                        onClick={(e) => { e.stopPropagation(); handleDuplicate(p) }}>
                  Duplicate
                </button>
                <button className="ac-btn-sm" title="Edit"
                        onClick={(e) => { e.stopPropagation(); setEditing(editing?.id === p.id ? null : p); setCreating(false) }}>
                  Edit
                </button>
                <button className="ac-btn-sm" title="Delete"
                        onClick={(e) => { e.stopPropagation(); handleDelete(p.id) }}>
                  &times;
                </button>
              </span>
              {editing?.id === p.id && (
                <div className="cv-dd-form">
                  <ProjectForm clubs={clubs} busy={busy} error={formError}
                    initial={p}
                    onSave={(t, o, g) => handleUpdate(p.id, t, o, g)}
                    onCancel={() => setEditing(null)} />
                </div>
              )}
            </div>
          ))}

          {sharedProjects.length > 0 && (
            <>
              <p className="cv-dd-section muted">Shared with you</p>
              {sharedProjects.map((p) => (
                <div key={p.id} className={`cv-dd-item${active?.id === p.id ? ' is-active' : ''}`}>
                  <button className="cv-dd-item__select" onClick={() => {
                    onSelect(p); setOpen(false); setEditing(null); setCreating(false)
                  }}>
                    <span className="cv-dd-item__title">{p.title}</span>
                    {p.owner_name && <span className="ac-badge">{p.owner_name}</span>}
                    {p.game_code && (
                      <span className="muted">{clubName(p.org_id)} &mdash; {p.game_code}</span>
                    )}
                  </button>
                  <span className="cv-dd-item__actions">
                    <button className="ac-btn-sm"
                            title="Take your own editable copy of this shared project"
                            onClick={(e) => { e.stopPropagation(); handleDuplicate(p) }}>
                      Duplicate
                    </button>
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline form for create / edit
// ---------------------------------------------------------------------------

function ProjectForm({ clubs, busy, error, initial, onSave, onCancel }: {
  clubs: CachedClub[]
  busy: boolean
  error: string | null
  initial?: AnalyticsProject
  onSave: (title: string, orgId: number | null, gameCode: string | null) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [orgId, setOrgId] = useState<number | ''>(initial?.org_id ?? '')
  const [gameCode, setGameCode] = useState(initial?.game_code ?? '')
  const [games, setGames] = useState<CachedGame[]>([])
  const [loadingGames, setLoadingGames] = useState(false)

  // Fetch games whenever the selected club changes
  useEffect(() => {
    if (!orgId) { setGames([]); return }
    setLoadingGames(true)
    getClubGames(orgId as number)
      .then(setGames)
      .catch(() => setGames([]))
      .finally(() => setLoadingGames(false))
  }, [orgId])

  return (
    <div className="ac-form" onClick={(e) => e.stopPropagation()}>
      <label>Title
        <input type="text" value={title} placeholder="e.g. Pressing Analysis"
               onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label>Club
        <select value={orgId} onChange={(e) => { setOrgId(e.target.value ? Number(e.target.value) : ''); setGameCode('') }}>
          <option value="">Any club</option>
          {clubs.map((c) => <option key={c.id} value={c.id}>{c.code} &middot; {c.name}</option>)}
        </select>
      </label>
      <label>Game
        <select value={gameCode} disabled={!orgId || loadingGames}
                onChange={(e) => setGameCode(e.target.value)}>
          <option value="">
            {!orgId ? 'Pick a club first' : loadingGames ? 'Loading games\u2026' : 'Select a game'}
          </option>
          {games.map((g) => (
            <option key={g.game_code} value={g.game_code}>
              {g.game_code}{g.date ? ` \u00b7 ${g.date}` : ''}
            </option>
          ))}
        </select>
      </label>
      <div className="ac-form__actions">
        <button className="ac-btn" disabled={busy || !title.trim()}
                onClick={() => onSave(title.trim(), orgId || null, gameCode.trim() || null)}>
          {initial ? 'Save' : 'Create'}
        </button>
        <button className="ac-btn-sm" onClick={onCancel}>Cancel</button>
      </div>
      {error && <p className="lab-error">{error}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Static analysis plan — the attacking structure reference
// ---------------------------------------------------------------------------

